import Typography from '@mui/material/Typography'
import { styled } from '@mui/system'

import LoadingWrapper from '../components/LoadingWrapper'
import { LuneTheme } from '../theme'

import Button from './Button'
import Tile from './Tile'

type ImageUploadProps = {
    imgSrc?: string
    title?: string
    /**
     * Supporting text shown below `title`.
     *
     * This may be a short phrase or sentence. Defaults to
     * "JPEG or PNG, 500 by 500 px, up to 200kb.".
     */
    description?: string
    /**
     * Label used for the upload button on the right side.
     *
     * This should usually be a short button label without trailing punctuation.
     * Defaults to "Upload image".
     */
    uploadButtonLabel?: string
    /**
     * Alt text for the uploaded image preview.
     *
     * This should describe the preview image for assistive technologies.
     * Defaults to "Uploaded Image".
     */
    imageAltText?: string
    isUploading: boolean
    onButtonClick: () => void
}

const ImageUpload = ({
    imgSrc,
    title,
    description,
    uploadButtonLabel,
    imageAltText,
    isUploading,
    onButtonClick,
}: ImageUploadProps) => {
    const Image = styled('img')(
        LuneTheme.unstable_sx({
            width: `48px`,
            height: `48px`,
            objectFit: 'cover',
        }),
    )

    const Placeholder = styled('div')(
        LuneTheme.unstable_sx({
            width: `48px`,
            height: `48px`,
            backgroundColor: 'Grey100',
            borderRadius: '50px',
        }),
    )

    return (
        <Tile
            icon={
                <LoadingWrapper sx={{ height: `48px`, width: `48px` }} loading={isUploading}>
                    {imgSrc ? (
                        <Image src={imgSrc} alt={imageAltText ?? 'Uploaded Image'} />
                    ) : (
                        <Placeholder />
                    )}
                </LoadingWrapper>
            }
            rightSide={
                <Button onClick={onButtonClick} disabled={isUploading} variant="text">
                    {uploadButtonLabel ?? 'Upload image'}
                </Button>
            }
        >
            <div>
                <Typography variant="body1">{title || `Company logo`}</Typography>
                <Typography variant="body3" color={'Grey600'}>
                    {description ?? 'JPEG or PNG, 500 by 500 px, up to 200kb.'}
                </Typography>
            </div>
        </Tile>
    )
}

export default ImageUpload
